import React, { useState, useRef, useEffect } from 'react';
import { Stop } from '../types/ferry';
import { useLanguage } from '../contexts/LanguageContext';

interface StopSelectorProps {
  stops: { [key: string]: Stop };
  selectedStop: string;
  onStopSelect: (stopId: string) => void;
  placeholder: string;
  label: string;
}

export const StopSelector: React.FC<StopSelectorProps> = ({
  stops,
  selectedStop,
  onStopSelect,
  placeholder,
  label
}) => {
  const { language, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const stopList = Object.values(stops);
  const selectedStopData = selectedStop ? stops[selectedStop] : null;

  // Check if a stop matches the query
  const matchesQuery = (stop: Stop, query: string): boolean => {
    if (!query) return false;

    const lowerQuery = query.toLowerCase();

    // Match against English name
    const englishName = stop.name.toLowerCase();
    if (englishName.startsWith(lowerQuery)) return true;

    // For Chinese language, match against pinyin
    if (language === 'zh' && stop.pinyin) {
      if (stop.pinyin.toLowerCase().startsWith(lowerQuery)) return true;
    }

    // Match against translated Chinese name
    const translatedName = t.getStopName(stop.name).toLowerCase();
    if (translatedName.startsWith(lowerQuery)) return true;

    return false;
  };

  // Sort stops based on input value - matching stops first, then others
  const sortStopsByRelevance = (stops: Stop[], query: string): Stop[] => {
    if (!query) return stops;

    // Separate matching and non-matching stops
    const matching: Stop[] = [];
    const nonMatching: Stop[] = [];

    stops.forEach(stop => {
      if (matchesQuery(stop, query)) {
        matching.push(stop);
      } else {
        nonMatching.push(stop);
      }
    });

    // Return matching stops first, then non-matching
    return [...matching, ...nonMatching];
  };

  // Group stops by location/island
  const groupStops = () => {
    // If there's a search query, don't group - just return flat list with matching stops first
    if (inputValue) {
      const sortedStops = sortStopsByRelevance(stopList, inputValue);
      // Return as a single "ungrouped" group
      return { 'All': sortedStops };
    }

    // No search query - group stops normally
    const groups: { [key: string]: Stop[] } = {
      'Mainland': [],
      'Naoshima': [],
      'Teshima': [],
      'Shodoshima': [],
      'Other Islands': []
    };

    stopList.forEach(stop => {
      const originalName = stop.name;

      if (!stop.island) {
        groups['Mainland'].push(stop);
      } else if (originalName.includes('Naoshima')) {
        groups['Naoshima'].push(stop);
      } else if (originalName.includes('Teshima')) {
        groups['Teshima'].push(stop);
      } else if (originalName.includes('Shodoshima')) {
        groups['Shodoshima'].push(stop);
      } else {
        groups['Other Islands'].push(stop);
      }
    });

    // Remove empty groups and return in order
    const orderedGroups: { [key: string]: Stop[] } = {};
    ['Mainland', 'Naoshima', 'Teshima', 'Shodoshima', 'Other Islands'].forEach(groupName => {
      if (groups[groupName].length > 0) {
        orderedGroups[groupName] = groups[groupName].sort((a, b) =>
          t.getStopName(a.name).localeCompare(t.getStopName(b.name))
        );
      }
    });

    return orderedGroups;
  };

  const groupedStops = groupStops();

  // Helper function to get Chinese group names (for future use)
  // const getChineseGroupName = (groupName: string): string => {
  //   const translations: { [key: string]: string } = {
  //     'Mainland': '本土',
  //     'Naoshima': '直岛',
  //     'Teshima': '丰岛', 
  //     'Shodoshima': '小豆岛',
  //     'Other Islands': '其他岛屿'
  //   };
  //   return translations[groupName] || groupName;
  // };

  // Helper function to render stop name with proper formatting
  const renderStopName = (stopName: string, className?: string) => {
    const translatedName = t.getStopName(stopName);
    const hasParentheses = translatedName.includes('(') && translatedName.includes(')');
    
    if (hasParentheses) {
      const [mainName, parenthetical] = translatedName.split('(');
      return (
        <span className={className}>
          {mainName.trim()}
          <span className="text-gray-500 text-sm ml-1">({parenthetical}</span>
        </span>
      );
    } else {
      return <span className={className}>{translatedName}</span>;
    }
  };

  // Update input value when selected stop changes
  useEffect(() => {
    if (selectedStopData) {
      setInputValue(t.getStopName(selectedStopData.name));
    } else {
      setInputValue('');
    }
  }, [selectedStop, selectedStopData, t, language]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <label className={`block text-base font-medium text-gray-700 mb-1 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
        {label}
      </label>

      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setIsOpen(true);
          }}
          onFocus={(e) => {
            setIsOpen(true);
            // Select all text so user can immediately type to replace
            e.target.select();
          }}
          placeholder={placeholder}
          className={`w-full bg-white border border-gray-300 rounded-lg px-4 py-3 pr-10 text-left shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${selectedStopData && !inputValue ? `text-gray-500 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}` : `text-gray-900 ${language === 'zh' ? 'font-chinese-serif' : 'font-ferry-place'}`}`}
        />
        <button
          type="button"
          onClick={() => {
            setIsOpen(!isOpen);
            if (!isOpen) {
              inputRef.current?.focus();
            }
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors"
          tabIndex={-1}
        >
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg">
          <div className="max-h-60 overflow-y-auto">
            {Object.entries(groupedStops).map(([groupName, stopsInGroup]) => (
              <div key={groupName}>
                {stopsInGroup.map((stop) => (
                  <button
                    key={stop.id}
                    onClick={() => {
                      onStopSelect(stop.id);
                      setInputValue(t.getStopName(stop.name));
                      setIsOpen(false);
                    }}
                    className={`w-full px-4 py-1.5 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors ${
                      selectedStop === stop.id ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
                    }`}
                  >
                    <div className={`font-medium ${language === 'zh' ? 'font-chinese-serif' : 'font-ferry-place'}`}>
                      {renderStopName(stop.name)}
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};